/**
 * Which custom studies share a pane under the price chart, and how tall each pane is.
 */

import groupBy from "lodash-es/groupBy";

/** One study to create on the chart. */
export interface StudyRequest {
  name: string;
  inMainChart: boolean;
  lock: boolean;
  /** The strategy chart panel the study is declared in. A pane study without one gets a pane of its own. */
  panel?: string | null;
}

/** The share of the chart height a pane gets when no usable share is given for it. */
export const DEFAULT_PANE_SHARE = 0.15;

/** The share of the chart height the price pane keeps, at least. */
export const PRICE_PANE_MIN_SHARE = 0.5;

/** Pane studies with the same key go in one pane: the key is the panel, or the study itself without one. */
export const paneKey = (request: StudyRequest) =>
  request.panel ? `panel:${request.panel}` : `study:${request.name}`;

/**
 * The pane studies in the order to create them: the studies of one pane next to each other, and the panes in
 * the order of their first study.
 */
export const orderPaneRequests = (requests: readonly StudyRequest[]): StudyRequest[] =>
  // groupBy adds a key when it first meets it, and Object.values keeps that order for every key that is not
  // an integer-like string, which a pane key never is
  Object.values(groupBy(requests, paneKey)).flat();

/**
 * Heights for every pane, the price pane first. Each other pane gets its share of the total: the share
 * given for it when that is a finite positive number, else DEFAULT_PANE_SHARE. When those shares add up to more
 * than what PRICE_PANE_MIN_SHARE leaves, they are scaled down in proportion. The price pane gets the rest.
 */
export const paneHeights = (total: number, shares: readonly (number | undefined)[]): number[] => {
  const wanted = shares.map((share) =>
    share !== undefined && Number.isFinite(share) && share > 0 ? share : DEFAULT_PANE_SHARE,
  );
  const sum = wanted.reduce((acc, share) => acc + share, 0);
  const room = 1 - PRICE_PANE_MIN_SHARE;
  const scale = sum > room ? room / sum : 1;
  // Rounded down, so the price pane never ends up below its minimum
  const heights = wanted.map((share) => Math.floor(total * share * scale));
  return [total - heights.reduce((acc, height) => acc + height, 0), ...heights];
};
